/**
 * Equipment Slot Button
 * Shared component used by PlayerEquipmentManager and BuddyEquipmentModal
 */

import { EquipmentSlot, EquipmentDefinition } from '@/types';
import { SLOT_CONFIG } from '@/data/equipment/slotConfig';

export interface EquipmentSlotButtonProps {
  slot: EquipmentSlot;
  equipped: EquipmentDefinition | null;
  isSelected: boolean;
  showTooltip?: boolean;
  onClick: () => void;
  onUnequip: () => void;
}

export function EquipmentSlotButton({ slot, equipped, isSelected, showTooltip = false, onClick }: EquipmentSlotButtonProps) {
  const icon = SLOT_CONFIG[slot].icon;
  const pos = SLOT_CONFIG[slot].silhouettePosition;
  const tooltipRight = pos === 'right' || pos === 'bottom-right';
  const tooltipPos = tooltipRight
    ? 'right-full mr-2'
    : 'left-full ml-2';

  return (
    <div className="relative group hover:z-50">
      <button
        onClick={onClick}
        className={`w-16 h-16 rounded-lg border-2 flex items-center justify-center text-2xl transition-all ${
          isSelected
            ? 'bg-blue-600 border-blue-400 scale-110 shadow-lg shadow-blue-500/50'
            : equipped
            ? 'bg-gray-700 border-gray-500 hover:border-gray-400'
            : 'bg-gray-800/50 border-gray-600 hover:border-gray-500'
        }`}
      >
        {equipped ? equipped.icon || icon : icon}
      </button>

      {/* Tooltip — visible on hover (desktop) and when selected (mobile tap) */}
      {equipped && (
        <div className={`absolute z-50 ${showTooltip ? 'block' : 'hidden group-hover:block'} ${tooltipPos} top-1/2 -translate-y-1/2 w-48 bg-gray-900 border border-gray-600 rounded-lg p-2 shadow-xl`}>
          <div className="text-xs">
            <div className="font-bold text-white">{equipped.name}</div>
            <div className="text-gray-400 mt-1">{equipped.description}</div>
            <div className="mt-2 space-y-1">
              {equipped.stats.fishingSpeed && (
                <div className="text-green-400">Speed +{Math.round(equipped.stats.fishingSpeed * 100)}%</div>
              )}
              {equipped.stats.catchChance && (
                <div className="text-blue-400">Catch +{Math.round(equipped.stats.catchChance * 100)}%</div>
              )}
              {equipped.stats.qualityBonus && (
                <div className="text-purple-400">Quality +{Math.round(equipped.stats.qualityBonus * 100)}%</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Slot Label — shows equipped item name when equipped, slot name when empty */}
      <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs whitespace-nowrap max-w-20 truncate text-center">
        {equipped ? (
          <span className="text-gray-200">{equipped.name}</span>
        ) : (
          <span className="text-gray-400">{SLOT_CONFIG[slot].label}</span>
        )}
      </div>
    </div>
  );
}
